// The withdrawal dialog (lane B8, S12; CR-035): "Withdrawal requires reason/stage/date." The reason is the published
// withdrawal list (a reason that needs text shows the note field), the stage is where the candidate stands now (the
// Offer stage on this page, read-only: the server records the stage it finds), and the date is when the candidate
// withdrew. Confirming records it through candidate.withdraw (B3's door); the caller then asks for the next path
// through offer.choose_next_path (NextPathDialog), because a withdrawal at offer is a decision about the requisition too.

import { useState } from "react";
import {
  ACTION_LABELS, Dialog, Field, G1_LABELS, Input, REFCHECK_LABELS, RULE_LABELS, SCREEN_LABELS, SCREEN_STATUS, Select, SURFACE_LABELS,
  Tag, TAG_LABELS, TextArea, type Blocked,
} from "@/mc";
import { needsNote, type DialogShell } from "./DecisionDialog";
import { OFFER_RULE_LABELS } from "./labels";
import type { ReasonOption } from "./types";

export interface WithdrawalDraft {
  reason: string;
  note: string;
  date: string;
}

export interface WithdrawalInput {
  reason_code: string;
  reason_text: string | null;
  /** The business-local date the candidate withdrew (YYYY-MM-DD); the caller turns it into an instant. */
  date: string;
}

const filled = (value: string | null | undefined): boolean => !!value && value.trim().length > 0;

/** Reason, stage and date: the stage is always present here (the page's own), so the reason and the date decide. */
export function withdrawalBlocked(draft: WithdrawalDraft, reasons: readonly ReasonOption[] | null | undefined, stage: string): Blocked | undefined {
  if (!filled(draft.reason) || !filled(stage) || !filled(draft.date)) return { reason: RULE_LABELS.withdrawalRequires };
  if (needsNote(reasons, draft.reason) && !filled(draft.note)) return { reason: RULE_LABELS.withdrawalRequires };
  return undefined;
}

export interface WithdrawalDialogProps extends DialogShell {
  reasons: readonly ReasonOption[] | null | undefined;
  /** The candidate's current stage, in his words (the Offer stage on this page). */
  stage: string;
  initial?: Partial<WithdrawalDraft> | undefined;
  onSubmit: (input: WithdrawalInput) => void;
}

export function WithdrawalDialog({ open, onClose, busy, refused, preview, reasons, stage, initial, onSubmit }: WithdrawalDialogProps) {
  const [draft, setDraft] = useState<WithdrawalDraft>({ reason: "", note: "", date: "", ...initial });
  const blocked = withdrawalBlocked(draft, reasons, stage);
  const note = needsNote(reasons, draft.reason);
  const submit = () => {
    if (blocked) return;
    onSubmit({ reason_code: draft.reason, reason_text: filled(draft.note) ? draft.note.trim() : null, date: draft.date });
  };
  return (
    <Dialog
      title={SCREEN_STATUS.withdrawn} open={open} onClose={onClose} preview={preview} dismissLabel={ACTION_LABELS.cancel}
      primary={{ label: REFCHECK_LABELS.record, onClick: submit, blocked, busy, hs: "offer.confirm" }}
      refused={refused ? { reason: refused } : undefined} hint={G1_LABELS.reasonStored} width={560}
    >
      <div className="mc-offer-form">
        <Field label={G1_LABELS.withdrawalReason} required>
          <Select options={(reasons ?? []).map((item) => ({ value: item.value, label: item.label }))} value={draft.reason}  onChange={(value) => setDraft({ ...draft, reason: value })} />
        </Field>
        {note ? (
          <Field label={SURFACE_LABELS.notes} required labelSlot={<Tag tone="internal">{TAG_LABELS.internal}</Tag>} hint={OFFER_RULE_LABELS.noInternalNotes}>
            <TextArea rows={3} value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
          </Field>
        ) : null}
        <Field label={SCREEN_LABELS.withdrawalStage} required>
          <Input value={stage} readOnly  />
        </Field>
        <Field label={SCREEN_LABELS.withdrawalDate} required>
          <Input type="date" value={draft.date}  onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}
